import React, {
  MouseEvent,
  useEffect,
} from 'react';
import {
  Table,
} from '@tanstack/react-table';
import clsx from 'clsx';
import IconButton from '@mui/material/IconButton';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandRoundedIcon from '@mui/icons-material/ExpandRounded';
import CompressRoundedIcon from '@mui/icons-material/CompressRounded';
import ToolTip from 'core/components/Tooltip';

type ExpandRowButtonProps = {
  expandable: boolean;
  isExpanded: boolean;
  onToggle: (event: MouseEvent<HTMLButtonElement>) => void;
};

export function ExpandRowButton({
  expandable,
  isExpanded,
  onToggle,
}: ExpandRowButtonProps) {
  const Icon = isExpanded && expandable ? ExpandMoreIcon : ChevronRightIcon;
  const tooltip = isExpanded ? 'Collapse' : 'Expand';

  return (
    <ToolTip title={!expandable ? 'No service codes' : tooltip}>
      <IconButton
        onClick={onToggle}
        disabled={!expandable}
        className={clsx('!p-1', {
          '!cursor-not-allowed !pointer-events-auto opacity-30': !expandable,
        })}
        disableRipple={!expandable}
      >
        <Icon className="!text-gray-500" />
      </IconButton>
    </ToolTip>
  );
}

export function useExpandAllDetails<T>(
  table: Table<T>,
  requestDetails: (ids: number[]) => void,
  getId: (row: T) => number,
): void {
  const isAllRowsExpanded = table.getIsAllRowsExpanded();
  const expandableIdsKey = table.getCoreRowModel().rows
    .filter((tableRow) => tableRow.getCanExpand())
    .map((tableRow) => getId(tableRow.original))
    .filter((id) => id > 0)
    .join(',');

  useEffect(() => {
    if (isAllRowsExpanded && expandableIdsKey.length > 0) {
      requestDetails(expandableIdsKey.split(',').map(Number));
    }
  }, [isAllRowsExpanded, expandableIdsKey, requestDetails]);
}

type ExpandAllButtonProps<T> = {
  table: Table<T>;
};

export function ExpandAllButton<T>({
  table,
}: ExpandAllButtonProps<T>) {
  const isAllRowsExpanded = table.getIsAllRowsExpanded();
  const Icon = isAllRowsExpanded ? CompressRoundedIcon : ExpandRoundedIcon;

  return (
    <ToolTip
      title={isAllRowsExpanded ? 'Collapse all' : 'Expand all'}
      disableFocusListener={false}
    >
      <IconButton
        disableRipple
        className="!p-1"
        onClick={(event) => {
          table.getToggleAllRowsExpandedHandler()(event);
        }}
      >
        <Icon className="!text-gray-500 !h-4 !w-4 relative left-1 top-0.5" />
      </IconButton>
    </ToolTip>
  );
}
